/**
 * Haptic Feedback Utilities
 * Provides tactile feedback on native platforms
 */

import { isNativePlatform } from '../config';

type ImpactStyle = 'heavy' | 'medium' | 'light';
type NotificationType = 'success' | 'warning' | 'error';

let hapticsModule: any = null;

/**
 * Lazy load the haptics module
 */
async function getHaptics(): Promise<any> {
  if (!isNativePlatform()) return null;

  if (!hapticsModule) {
    try {
      const module = await import('@capacitor/haptics');
      hapticsModule = module.Haptics;
    } catch (err) {
      console.warn('Haptics not available:', err);
      return null;
    }
  }
  return hapticsModule;
}

/**
 * Trigger an impact haptic feedback
 * Use for button presses, selections, etc.
 */
export async function impact(style: ImpactStyle = 'medium'): Promise<void> {
  const Haptics = await getHaptics();
  if (!Haptics) return;

  try {
    await Haptics.impact({ style });
  } catch (err) {
    // Silently fail - haptics are non-essential
  }
}

/**
 * Trigger a notification haptic feedback
 * Use for success/warning/error states
 */
export async function notification(type: NotificationType = 'success'): Promise<void> {
  const Haptics = await getHaptics();
  if (!Haptics) return;

  try {
    await Haptics.notification({ type });
  } catch (err) {
    // Silently fail
  }
}

/**
 * Trigger a simple vibration
 */
export async function vibrate(duration: number = 300): Promise<void> {
  const Haptics = await getHaptics();
  if (!Haptics) return;

  try {
    await Haptics.vibrate({ duration });
  } catch (err) {
    // Silently fail
  }
}

/**
 * Selection feedback for pickers, sliders, etc.
 */
export async function selectionChanged(): Promise<void> {
  const Haptics = await getHaptics();
  if (!Haptics) return;

  try {
    await Haptics.selectionChanged();
  } catch (err) {
    // Silently fail
  }
}

// Convenience exports for common use cases
export const light = () => impact('light');
export const medium = () => impact('medium');
export const heavy = () => impact('heavy');
export const success = () => notification('success');
export const warning = () => notification('warning');
export const error = () => notification('error');

export default {
  impact,
  notification,
  vibrate,
  selectionChanged,
  light,
  medium,
  heavy,
  success,
  warning,
  error,
};
